import { useEffect, useRef } from 'react';
import { supabase } from '../supabase';

/**
 * useGlobalPresence — Tracks the current user on a shared Supabase Realtime
 * Presence channel (`presence:global`) so the admin dashboard can see who
 * is currently online across all tenants.
 *
 * Every authenticated client calls this hook once (in AuthedApp).  When the
 * component unmounts or the user signs out, the channel is removed and
 * presence is automatically cleared by the Supabase realtime heartbeat
 * (~60s timeout).
 *
 * The presence key is the user's id so multiple tabs from the same user
 * are deduplicated to a single entry.
 *
 * Presence event listeners are registered BEFORE `.subscribe()` (Supabase
 * requires this) and update a module-level `_liveUsers` array + notify any
 * registered `useLiveUsers` subscribers.  This avoids the "cannot add
 * callbacks after subscribe" error that would occur if `useLiveUsers`
 * tried to call `.on()` on the same singleton channel.
 *
 * @param {Object|null} user — The enriched user object from App.jsx
 *   (requires: id, name, email, role, tenantId)
 */

// ── Module-level shared state ──────────────────────────────────────────
// Allows useLiveUsers to read the presence state without creating its
// own channel (which would conflict with the singleton).
// `_liveUsersSnapshot` is replaced with a NEW array reference on every
// presence change so `useSyncExternalStore` detects the update.
let _liveUsersSnapshot: { user_id: string; user_name: string; tenant_id: string; role: string; email: string; joined_at: number | null }[] = [];
const _liveUsersListeners = new Set<() => void>();

function flattenPresence(state: Record<string, any>) {
    const flat: { user_id: string; user_name: string; tenant_id: string; role: string; email: string; joined_at: number | null }[] = [];
    Object.values(state).forEach((presences) => {
        if (presences && presences.length > 0) {
            const p = presences[0];
            if (p && p.user_id) {
                flat.push({
                    user_id: p.user_id,
                    user_name: p.user_name || '—',
                    tenant_id: p.tenant_id || '—',
                    role: p.role || '—',
                    email: p.email || '',
                    joined_at: p.joined_at || null,
                });
            }
        }
    });
    flat.sort((a, b) => {
        const t = (a.tenant_id || '').localeCompare(b.tenant_id || '');
        if (t !== 0) return t;
        return (a.user_name || '').localeCompare(b.user_name || '');
    });
    return flat;
}

function notifyLiveUsersListeners(state: Record<string, any>) {
    _liveUsersSnapshot = flattenPresence(state);
    _liveUsersListeners.forEach((fn) => fn());
}

export function useGlobalPresence(user: { id?: string; name?: string; email?: string; role?: string; tenantId?: string } | null | undefined) {
    const channelRef = useRef<import('@supabase/supabase-js').RealtimeChannel | null>(null);

    useEffect(() => {
        if (!user?.id || !user?.tenantId) return;

        const channel = supabase.channel('presence:global', {
            config: { presence: { key: user.id } },
        });

        // Register presence listeners BEFORE subscribe (Supabase requirement).
        channel.on('presence', { event: 'sync' }, () => {
            notifyLiveUsersListeners(channel.presenceState());
        });
        channel.on('presence', { event: 'join' }, () => {
            notifyLiveUsersListeners(channel.presenceState());
        });
        channel.on('presence', { event: 'leave' }, () => {
            notifyLiveUsersListeners(channel.presenceState());
        });

        channel.subscribe(async (status) => {
            if (status !== 'SUBSCRIBED') return;
            try {
                await channel.track({
                    user_id: user.id,
                    user_name: user.name || '—',
                    tenant_id: user.tenantId,
                    role: user.role || '—',
                    email: user.email || '',
                    joined_at: Date.now(),
                });
            } catch (err) {
                console.error('[useGlobalPresence] Track error:', err);
            }
        });

        channelRef.current = channel;

        return () => {
            supabase.removeChannel(channel);
            channelRef.current = null;
        };
    }, [user?.id, user?.tenantId, user?.name, user?.role, user?.email]);
}

export { _liveUsersSnapshot, _liveUsersListeners, notifyLiveUsersListeners };

export default useGlobalPresence;
